import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MDXEditorMethods } from '@mdxeditor/editor';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useColorScheme, useTheme } from '@mui/material/styles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import LoadingIndicator from '@/components/LoadingIndicator';
import { PERMISSIONS } from '@/constants/auth.constants';
import type { OrgUser } from '@/pages/Administration/Administration.types';

import EPInternalNotes from './EPInternalNotes';
import EPP1EditContent from './EPP1EditContent';
import EPP1ViewContent from './EPP1ViewContent';
import EPPriorityUserChips from './EPPriorityUserChips';
import EPSmsContent from './EPSmsContent';
import { SMS_FIELD } from './EscalationProfile.constants';
import type {
  EscalationProfileFormValues,
  EscalationProfileProps
} from './EscalationProfile.types';

const EMPTY_IDS: number[] = [];

const EscalationProfile = ({
  isLoadingUsers,
  orgInfo,
  orgUsers,
  handleSave
}: EscalationProfileProps) => {
  const theme = useTheme();
  const { mode: themeMode } = useColorScheme();
  const escalationProfile = orgInfo.escalation_profile;
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null);
  const [selectedSmsUsers, setSelectedSmsUsers] = useState<
    Record<1 | 2 | 3 | 4, OrgUser | null>
  >({ 1: null, 2: null, 3: null, 4: null });
  const mdxRef = useRef<MDXEditorMethods>(null);

  const defaultValues = useMemo<EscalationProfileFormValues>(
    () => ({
      calling_enabled:
        (escalationProfile?.p1?.contact_method ?? 'call') !== 'none',
      call_order: escalationProfile?.p1?.call_order ?? [],
      sms_recipients_p1: escalationProfile?.p1?.sms_recipients ?? [],
      sms_recipients_p2: escalationProfile?.p2?.sms_recipients ?? [],
      sms_recipients_p3: escalationProfile?.p3?.sms_recipients ?? [],
      sms_recipients_p4: escalationProfile?.p4?.sms_recipients ?? [],
      internal_notes: escalationProfile?.internal_notes ?? ''
    }),
    [escalationProfile]
  );

  const { control, handleSubmit, reset, setValue } =
    useForm<EscalationProfileFormValues>({
      defaultValues,
      values: defaultValues
    });

  const callOrder = useWatch({ control, name: 'call_order' }) ?? EMPTY_IDS;
  const callingEnabled = useWatch({ control, name: 'calling_enabled' });
  const internalNotes = useWatch({ control, name: 'internal_notes' });
  const smsRecipients: Record<1 | 2 | 3 | 4, number[]> = {
    1: useWatch({ control, name: 'sms_recipients_p1' }) ?? EMPTY_IDS,
    2: useWatch({ control, name: 'sms_recipients_p2' }) ?? EMPTY_IDS,
    3: useWatch({ control, name: 'sms_recipients_p3' }) ?? EMPTY_IDS,
    4: useWatch({ control, name: 'sms_recipients_p4' }) ?? EMPTY_IDS
  };

  const callOrderUsers = useMemo(
    () =>
      callOrder
        .map((id) => orgUsers.find((u) => u.id === id))
        .filter((u): u is OrgUser => !!u),
    [callOrder, orgUsers]
  );

  const missingPhoneUsers = useMemo(
    () =>
      callOrderUsers.filter((u) => !u.phone_number || u.phone_number === ''),
    [callOrderUsers]
  );

  const availableUsersForPicker = useMemo(
    () =>
      orgUsers
        .filter((u) => !callOrder.includes(u.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [callOrder, orgUsers]
  );

  const smsConfig = useMemo(() => {
    const sorted = [...orgUsers].sort((a, b) => a.name.localeCompare(b.name));
    const forPriority = (recipients: number[]) => ({
      smsUsers: recipients
        .map((id) => orgUsers.find((u) => u.id === id))
        .filter((u): u is OrgUser => !!u),
      availableUsers: sorted.filter((u) => !recipients.includes(u.id))
    });
    return {
      1: forPriority(smsRecipients[1]),
      2: forPriority(smsRecipients[2]),
      3: forPriority(smsRecipients[3]),
      4: forPriority(smsRecipients[4])
    };
  }, [orgUsers, smsRecipients]);

  const priorityUsers = useMemo<Record<number, OrgUser[]>>(() => {
    const ticketThreatUsers = orgUsers.filter(
      (u) => u.notification_ticket_threat === true
    );
    return {
      1: ticketThreatUsers.filter((u) =>
        u.notification_threat_priority.includes(1)
      ),
      2: ticketThreatUsers.filter((u) =>
        u.notification_threat_priority.includes(2)
      ),
      3: ticketThreatUsers.filter((u) =>
        u.notification_threat_priority.includes(3)
      ),
      4: ticketThreatUsers.filter((u) =>
        u.notification_threat_priority.includes(4)
      )
    };
  }, [orgUsers]);

  const onSubmit = useCallback(
    (data: EscalationProfileFormValues) => {
      const notes = mdxRef.current?.getMarkdown() ?? data.internal_notes;
      handleSave({
        ...orgInfo,
        escalation_profile: {
          p1: {
            contact_method: data.calling_enabled ? 'call' : 'none',
            call_order: data.calling_enabled ? data.call_order : [],
            sms_recipients: data.sms_recipients_p1
          },
          p2: {
            sms_recipients: data.sms_recipients_p2
          },
          p3: {
            sms_recipients: data.sms_recipients_p3
          },
          p4: {
            sms_recipients: data.sms_recipients_p4
          },
          internal_notes: notes || null
        }
      });
      setIsEditing(false);
    },
    [handleSave, mdxRef, orgInfo]
  );

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setSelectedUser(null);
    setSelectedSmsUsers({ 1: null, 2: null, 3: null, 4: null });
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleAddUser = useCallback(() => {
    if (selectedUser && !callOrder.includes(selectedUser.id)) {
      setValue('call_order', [...callOrder, selectedUser.id]);
      setSelectedUser(null);
    }
  }, [callOrder, selectedUser, setValue]);

  const handleRemoveUser = useCallback(
    (userId: number) => {
      setValue(
        'call_order',
        callOrder.filter((id) => id !== userId)
      );
    },
    [callOrder, setValue]
  );

  const handleMoveUser = useCallback(
    (userId: number, direction: 'up' | 'down') => {
      const idx = callOrder.indexOf(userId);
      if (idx === -1) return;

      const newOrder = [...callOrder];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= newOrder.length) return;

      [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
      setValue('call_order', newOrder);
    },
    [callOrder, setValue]
  );

  const handleCallingToggle = useCallback(
    (checked: boolean) => {
      if (!checked) setValue('call_order', []);
    },
    [setValue]
  );

  const setSmsRecipients = useCallback(
    (priority: 1 | 2 | 3 | 4, value: number[]) =>
      setValue(SMS_FIELD[priority], value),
    [setValue]
  );

  const handleAddSmsUser = useCallback(
    (priority: 1 | 2 | 3 | 4) => {
      const selected = selectedSmsUsers[priority];
      const current = smsRecipients[priority];
      if (selected && !current.includes(selected.id)) {
        setSmsRecipients(priority, [...current, selected.id]);
        setSelectedSmsUsers((prev) => ({ ...prev, [priority]: null }));
      }
    },
    [selectedSmsUsers, smsRecipients, setSmsRecipients]
  );

  const handleRemoveSmsUser = useCallback(
    (priority: 1 | 2 | 3 | 4, userId: number) => {
      setSmsRecipients(
        priority,
        smsRecipients[priority].filter((id) => id !== userId)
      );
    },
    [smsRecipients, setSmsRecipients]
  );

  const handleSaveClick = useCallback(
    () => handleSubmit(onSubmit)(),
    [handleSubmit, onSubmit]
  );

  return (
    <LargeCard
      title="Escalation Profile"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {isEditing ? (
            <Stack direction="row">
              <IconButton onClick={handleSaveClick}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      {isLoadingUsers ? (
        <LoadingIndicator isLoading />
      ) : (
        <Stack spacing={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.vars.palette.background.default
                  }}
                >
                  <TableCell sx={{ width: '15rem' }}>Priority</TableCell>
                  <TableCell>Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>P1</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configurable Call &amp; SMS Notifications
                      <br />
                      for New Tickets
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, verticalAlign: 'top' }}>
                    <Stack sx={{ gap: 3 }}>
                      {isEditing ? (
                        <EPP1EditContent
                          availableUsersForPicker={availableUsersForPicker}
                          control={control}
                          callingEnabled={callingEnabled}
                          callOrderUsers={callOrderUsers}
                          missingPhoneUsers={missingPhoneUsers}
                          onCallingToggle={handleCallingToggle}
                          onAddUser={handleAddUser}
                          onMoveUser={handleMoveUser}
                          onRemoveUser={handleRemoveUser}
                          p1EmailUsers={priorityUsers[1]}
                          selectedUser={selectedUser}
                          setSelectedUser={setSelectedUser}
                        />
                      ) : (
                        <EPP1ViewContent
                          callingEnabled={callingEnabled}
                          callOrderUsers={callOrderUsers}
                          missingPhoneUsers={missingPhoneUsers}
                          p1EmailUsers={priorityUsers[1]}
                        />
                      )}
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        sx={{ gap: 3 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <EPSmsContent
                            priority={1}
                            isEditing={isEditing}
                            smsUsers={smsConfig[1].smsUsers}
                            availableUsers={smsConfig[1].availableUsers}
                            selectedUser={selectedSmsUsers[1]}
                            setSelectedUser={(user) =>
                              setSelectedSmsUsers((prev) => ({
                                ...prev,
                                1: user
                              }))
                            }
                            onAddUser={() => handleAddSmsUser(1)}
                            onRemoveUser={(userId) =>
                              handleRemoveSmsUser(1, userId)
                            }
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', pb: 1 }}>
                            Auto-notified via Email
                          </Typography>
                          <EPPriorityUserChips
                            users={priorityUsers[1]}
                            priority={1}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </TableCell>
                </TableRow>
                {([2, 3, 4] as const).map((priority) => (
                  <TableRow key={priority}>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        P{priority}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configurable SMS Notifications
                        <br /> for New Tickets
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        sx={{
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: 3
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <EPSmsContent
                            priority={priority}
                            isEditing={isEditing}
                            smsUsers={smsConfig[priority].smsUsers}
                            availableUsers={smsConfig[priority].availableUsers}
                            selectedUser={selectedSmsUsers[priority]}
                            setSelectedUser={(user) =>
                              setSelectedSmsUsers((prev) => ({
                                ...prev,
                                [priority]: user
                              }))
                            }
                            onAddUser={() => handleAddSmsUser(priority)}
                            onRemoveUser={(userId) =>
                              handleRemoveSmsUser(priority, userId)
                            }
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', pb: 1 }}>
                            Auto-notified via Email
                          </Typography>
                          <EPPriorityUserChips
                            users={priorityUsers[priority]}
                            priority={priority}
                          />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
            <EPInternalNotes
              isEditing={isEditing}
              control={control}
              mdxRef={mdxRef}
              themeMode={themeMode}
              internalNotes={internalNotes ?? ''}
            />
          </CheckPermissions>
        </Stack>
      )}
    </LargeCard>
  );
};

export default EscalationProfile;
